import { useDispatch, batch } from "react-redux";
import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { RaceProgram, Runner, BettingInterest } from "@tvg/ts-types/Race";
import { addToast, removeToast } from "../store/actions";

interface UseScratchedRunnersProps {
  race: RaceProgram;
}

interface ScratchedRunnersInterface {
  runner: Runner;
  toastShown: boolean;
  toastId: string;
}

const getScratchedFromBi = ({
  bettingInterests,
  shouldShowToast
}: {
  bettingInterests: BettingInterest[];
  shouldShowToast: boolean;
}) =>
  bettingInterests.reduce(
    (
      scratchedRunnersAcc: ScratchedRunnersInterface[],
      bettingInterest: BettingInterest
    ) => {
      const scratcheds = bettingInterest.runners ? bettingInterest.runners.filter(
        (runner) => runner.scratched
      ) : [];

      if (scratcheds.length) {
        scratchedRunnersAcc.push(
          ...scratcheds.map((runner) => ({
            runner,
            toastShown: !shouldShowToast,
            toastId: uuid()
          }))
        );
      }

      return scratchedRunnersAcc;
    },
    []
  );

const useScratchedRunners = ({ race }: UseScratchedRunnersProps) => {
  const [scratchedRunners, setScratchedRunners] = useState<
    ScratchedRunnersInterface[]
  >([]);
  const [isBettingInterestLoaded, setIsBettingInterestLoaded] =
    useState<boolean>(false);
  const [currentRaceId, setCurrentRaceId] = useState<string>();
  const dispatch = useDispatch();

  useEffect(() => {
    if (currentRaceId === undefined && race.id) {
      setCurrentRaceId(race.id);
    }

    if (currentRaceId !== race.id) {
      setIsBettingInterestLoaded(false);
      setCurrentRaceId(race.id);
      setScratchedRunners([]);
    }
  }, [race.id]);

  useEffect(() => {
    if (currentRaceId === race.id) {
      if (
        race.bettingInterests &&
        scratchedRunners.length === 0 &&
        !isBettingInterestLoaded
      ) {
        setScratchedRunners(
          getScratchedFromBi({
            bettingInterests: race.bettingInterests,
            shouldShowToast: false
          })
        );
        setIsBettingInterestLoaded(true);
      }

      if (race.bettingInterests && isBettingInterestLoaded) {
        const scratchedFromBi = getScratchedFromBi({
          bettingInterests: race.bettingInterests,
          shouldShowToast: true
        });
        const newScratchedRunners: ScratchedRunnersInterface[] = [];
        scratchedFromBi.forEach((bi) => {
          const existingRunner = scratchedRunners.find(
            (scratchedRunner) =>
              scratchedRunner.runner.runnerId === bi.runner.runnerId
          );

          if (existingRunner) {
            newScratchedRunners.push(existingRunner);
          } else {
            newScratchedRunners.push(bi);
          }
        });

        if (newScratchedRunners.length) {
          setScratchedRunners(newScratchedRunners);
        }
      }
    }
  }, [JSON.stringify(race.bettingInterests)]);

  useEffect(() => {
    const notShownOnToast = scratchedRunners.filter(
      (scratchedRunner) => !scratchedRunner.toastShown
    );

    if (notShownOnToast.length) {
      batch(() => {
        notShownOnToast.forEach(({ runner, toastId }) => {
          dispatch(
            addToast({
              id: toastId,
              message: `Horse ${runner.horseName} has been scratched`,
              qaLabel: `scratched-horse-${runner.runnerId}-toast`,
              variant: "informational",
              hasDismissButton: true,
              onDismissPress: () => dispatch(removeToast(toastId)),
              onHide: () => dispatch(removeToast(toastId))
            })
          );
        });
      });

      setScratchedRunners((runners) =>
        runners.map((scratchedRunner) =>
          scratchedRunner.toastShown
            ? scratchedRunner
            : {
                ...scratchedRunner,
                toastShown: true
              }
        )
      );
    }
  }, [scratchedRunners]);
};

export default useScratchedRunners;
